import { ConnectedPosition } from "@angular/cdk/overlay";

/**
 * Ordered fallback positions, tried in turn by `flexibleConnectedTo` before the overlay is
 * pushed back into the viewport: below the target first, then above it. The card is always
 * horizontally centred on the viewport (FLIP-340 design review) - CoachMarkService sets each
 * position's offsetX at runtime to shift it from the target's centre to the viewport's, and moves
 * the pointer along the card's edge so it still points at the target.
 */
// offsetY clears the 20px the pointer sticks out from the card (.ob-modal__pointer) plus a 4px gap
// to the target - the overlay is measured by the card, not the absolutely-positioned pointer.
export const DEFAULT_COACH_MARK_POSITIONS: ConnectedPosition[] = [
  { originX: "center", originY: "bottom", overlayX: "center", overlayY: "top", offsetY: 24 },
  { originX: "center", originY: "top", overlayX: "center", overlayY: "bottom", offsetY: -24 },
];

/**
 * Distance from the card's top to the centre of a left/right pointer: `.ob-modal--pointer-left` /
 * `-right` sit at `top: 24px` and are 28px tall (onboarding-base-modal.component.scss). Keep in sync.
 */
export const SIDE_POINTER_CENTRE_PX = 38;

/**
 * "Mobile" for onboarding prompts: below the app's md breakpoint (768px, `bp.media-up("md")`) - the
 * widths where the side nav gives way to the bottom nav. Phones, the in-app webview and a mobile
 * browser all land here; tablets (768px and up) and desktop do not.
 */
export const MOBILE_VIEWPORT_QUERY = "(max-width: 767px)";

export function isMobileViewport(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(MOBILE_VIEWPORT_QUERY).matches;
}

/**
 * Default placement on tablet / desktop widths (>= 768px): the card stays near its target instead of
 * being centred on the viewport. Below the target first, then above, the card centred on the target so the
 * pointer sits in the middle of its edge. Near a viewport edge the card can't be centred, so each side has
 * an edge-aligned fallback: its left edge 48px before the target's centre (pointer 34px along the card's
 * edge, on the centre), then its right edge 48px after it. The centred and start-aligned entries only win
 * when the whole card fits, and the end-aligned one covers a target near the right edge - a start-aligned
 * card there would be squeezed by the space to its right before `withPush` could move it. Beside the target
 * (right, then left) is the last resort, for a target too tall to leave room above or below. `lockedSide`
 * keeps only the below / above entries (the side entries have `originY: "center"`). offsetY / offsetX clear
 * the pointer plus a 4px gap, as elsewhere.
 */
export const DESKTOP_ANCHORED_POSITIONS: ConnectedPosition[] = [
  { originX: "center", originY: "bottom", overlayX: "center", overlayY: "top", offsetY: 24 },
  { originX: "center", originY: "bottom", overlayX: "start", overlayY: "top", offsetX: -48, offsetY: 24 },
  { originX: "center", originY: "bottom", overlayX: "end", overlayY: "top", offsetX: 48, offsetY: 24 },
  { originX: "center", originY: "top", overlayX: "center", overlayY: "bottom", offsetY: -24 },
  { originX: "center", originY: "top", overlayX: "start", overlayY: "bottom", offsetX: -48, offsetY: -24 },
  { originX: "center", originY: "top", overlayX: "end", overlayY: "bottom", offsetX: 48, offsetY: -24 },
  { originX: "end", originY: "center", overlayX: "start", overlayY: "top", offsetX: 24, offsetY: -SIDE_POINTER_CENTRE_PX },
  { originX: "start", originY: "center", overlayX: "end", overlayY: "top", offsetX: -24, offsetY: -SIDE_POINTER_CENTRE_PX },
];

/**
 * For a target in a vertical rail (the desktop side nav): the card sits to the target's right, its
 * top offset so the side pointer - fixed at SIDE_POINTER_CENTRE_PX down the card - lands on the
 * target's vertical centre. offsetX clears the 20px the pointer sticks out plus a 4px gap.
 */
export const RIGHT_OF_TARGET_POSITIONS: ConnectedPosition[] = [
  { originX: "end", originY: "center", overlayX: "start", overlayY: "top", offsetX: 24, offsetY: -SIDE_POINTER_CENTRE_PX },
];

/**
 * For a target with room to its left and none to its right (the first Learn topic row, which spans
 * to the page's right edge): the card sits to the target's left, its top offset so the side pointer
 * lands on the target's vertical centre. Mirror of RIGHT_OF_TARGET_POSITIONS.
 */
export const LEFT_OF_TARGET_POSITIONS: ConnectedPosition[] = [
  { originX: "start", originY: "center", overlayX: "end", overlayY: "top", offsetX: -24, offsetY: -SIDE_POINTER_CENTRE_PX },
];

/**
 * For a target near the viewport's right edge (the last bottom-nav item): the card sits above it with
 * its right edge on the target's, extending left. The default viewport-centred placement can't reach
 * such a target - the pointer stops at the card's corner clearance - so this keeps the pointer under
 * the target's own centre. `withPush` keeps it in the viewport.
 */
export const ABOVE_TARGET_ALIGN_END_POSITIONS: ConnectedPosition[] = [
  { originX: "end", originY: "top", overlayX: "end", overlayY: "bottom", offsetY: -24 },
];

/**
 * For a target near the viewport's left edge (UP08's Favourite heart, first-but-one in the Plan
 * toolbar): the card sits above it with its left edge 40px before the target's centre, so the
 * pointer lands on the target's centre with room to spare - the viewport-centred placement can't
 * reach it, since the pointer stops at the card's corner clearance. `withPush` keeps it in the viewport.
 */
export const ABOVE_TARGET_NEAR_LEFT_EDGE_POSITIONS: ConnectedPosition[] = [
  { originX: "center", originY: "top", overlayX: "start", overlayY: "bottom", offsetX: -40, offsetY: -24 },
];

/**
 * Copy overrides for the unanchored fallback card, shown when the target can't be resolved in
 * time. The anchored-prompt contract says the fallback renders "the same copy", so both fields
 * default to the prompt's own heading/message; the card always carries the prompt's own actions
 * and close.
 */
export interface CoachMarkFallback {
  heading?: string;
  message?: string;
}

/** One button in the optional footer row - see CoachMarkPromptComponent's with/without-buttons variants. */
export interface CoachMarkAction {
  label: string;
  onClick: () => void;
}

export interface CoachMarkConfig {
  /** A key registered by `[flipCoachTarget]`, or a resolved element directly. */
  target: string | HTMLElement;
  heading: string;
  message: string;
  /**
   * Overrides the default placement (viewport-centred on mobile, DESKTOP_ANCHORED_POSITIONS from 768px
   * up) - used as given, with no centring offset applied.
   */
  positions?: ConnectedPosition[];
  /**
   * Like `positions`, but only from 768px up: mobile keeps the viewport-centred placement. Ignored
   * when `positions` is set.
   */
  desktopPositions?: ConnectedPosition[];
  /**
   * Extra px added to the default 24px gap (20px pointer + 4px) between the target and the pointer
   * tip. Keeps the viewport-centred behaviour, unlike `positions`. Positive pulls the prompt further
   * away; negative pulls it in, letting the card slightly overlap the target (the Plan card slots,
   * P03/P05, both use -32 for an ~8px overlap).
   */
  extraClearancePx?: number;
  /**
   * Locks the viewport-centred position to one side of the target instead of letting
   * `flexibleConnectedTo` fall back to the other side when the first choice doesn't fit
   * (DEFAULT_COACH_MARK_POSITIONS tries "below" first). Keeps centring, unlike `positions`.
   * `withPush(true)` still keeps the overlay in the viewport if the locked side is tight - this
   * only stops the *flip*, it doesn't change the ordinary "nearest" scroll-into-view. For a target
   * that should always read as "under"/"above" a specific slot regardless of scroll position (P05,
   * FLIP-356: the prompt must stay under the Star Card after-slot, not flip above it).
   */
  lockedSide?: "below" | "above";
  /**
   * Set false to leave the page where it is: no scroll to bring the target into view, clear of the
   * bottom nav, or to make room for the card. For a target the user has just tapped, where a scroll
   * would strand a fixed overlay that belongs to it (CA02's open card menu, FLIP-412). Default true.
   */
  scrollToTarget?: boolean;
  /** How long to keep retrying target resolution before degrading to the fallback. Default 2000ms. */
  resolveTimeoutMs?: number;
  /**
   * Copy for the unanchored bottom card shown when the target can't be resolved in time - omit to
   * reuse `heading` / `message` (the contract's "same copy"). The card has the same actions,
   * close and callbacks as the anchored prompt.
   */
  fallback?: CoachMarkFallback;
  /**
   * Identifies which onboarding lesson this coach-mark belongs to (FLIP-322). Distinct from
   * `target`, which is an anchor slot key, not guaranteed 1:1 with a lesson. Omitted for
   * non-onboarding callers, which stay untracked.
   */
  lessonId?: string;
  /**
   * Renders the "Anchored prompt with Buttons" variant when set (Onboarding Figma node 20:5582);
   * omit for "without Buttons" (node 20:6665). Each button's onClick fires before the prompt
   * dismisses itself - callers that need the dismiss-only outcome not to fire should not also
   * call `dismiss()` from inside onClick, since CoachMarkService already tears down after either
   * button is pressed.
   */
  primaryAction?: CoachMarkAction;
  secondaryAction?: CoachMarkAction;
  /**
   * Called when the user closes the prompt with its visible close control (FLIP-340). What that
   * means is the lesson's call - informational prompts complete, task prompts defer (§26.4) - so
   * when this is set the engine records only the close action and leaves the resulting lesson
   * state event to the caller, rather than its generic "dismissed".
   */
  onDismiss?: () => void;
  /**
   * Show straight away even while a dialog is open, instead of waiting for BlockingUiSignalService
   * to go quiet - for a prompt whose target lives *inside* an open dialog (P09 points at the Custom
   * card in the Star Card picker, FLIP-360). The overlay stacks above the dialog.
   */
  showOverDialogs?: boolean;
}
